import React, { ChangeEvent, FC, useState } from 'react';
import { MenuItem, Select, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { compact } from 'lodash';
import { useNotify } from 'hooks';

import { getAppImage } from 'helpers';
import {
  useCreateGroupWebhookWorkvivoMutation,
  useDeleteGroupWebhookMutation,
  Webhook,
  WorkvivoWorkspace,
} from 'api';
import { useWorkvivoWebhook } from 'features/groups/hooks';
import { useCreateGroupWebhook, useDeleteGroupWebhook } from 'features/groups/updaters';

interface Props {
  webhook: Webhook;
}

const WebhookItem: FC<Props> = ({ webhook }) => {
  const { t } = useTranslation();
  const { group_id } = useParams<{ group_id: string }>();
  const notify = useNotify();

  const { workvivo_workspaces, webhooks } = useWorkvivoWebhook();

  const deleteGroupWebhookMutationOptions = useDeleteGroupWebhook();
  const { mutate: deleteGroupWebhook } = useDeleteGroupWebhookMutation({
    onError: notify.mutationError,
    ...deleteGroupWebhookMutationOptions,
  });

  const createGroupWebhookMutationOptions = useCreateGroupWebhook();
  const { mutate: createGroupWebhook } = useCreateGroupWebhookWorkvivoMutation({
    onError: notify.mutationError,
    ...createGroupWebhookMutationOptions,
  });

  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkvivoWorkspace>(
    getSelectedWorkspace()
  );

  function getSelectedWorkspace() {
    const workspace = workvivo_workspaces.find(
      space => space.workvivo_workspace_id === webhook.workvivo_workspace_id
    );

    return workspace || { workspace_name: '', workvivo_workspace_id: '' };
  }

  function deleteWebhook() {
    webhooks.forEach(wh => {
      if (wh.groups.includes(group_id) && wh.workvivo_workspace_id) {
        deleteGroupWebhook({ group_id, webhook_id: webhook.webhook_id });
      }
    });
  }

  function handleSpaceChange(event: ChangeEvent<HTMLInputElement>) {
    const selected = workvivo_workspaces.find(
      (space: WorkvivoWorkspace) => space.workspace_name == event.target.value
    );

    if (!selected) {
      setSelectedWorkspace({ workspace_name: '', workvivo_workspace_id: '' });
      return deleteWebhook();
    }

    const updatedConfiguredWebhooks = webhooks.map(nextWebhook => {
      if (nextWebhook.webhook_id === webhook.webhook_id) {
        return {
          ...webhook,
          groups: compact([...(webhook?.groups || []), group_id]),
          workvivo_workspace_id: selected.workvivo_workspace_id,
        };
      }

      return nextWebhook;
    });

    updatedConfiguredWebhooks.forEach(nextWebhook => {
      createGroupWebhook({
        group_id,
        webhook_id: nextWebhook.webhook_id,
        workvivo_workspace_id: nextWebhook.workvivo_workspace_id,
      });
    });

    setSelectedWorkspace(selected);
  }

  return (
    <Grid container justifyContent="space-between" alignItems="center">
      <Grid>
        <Grid container alignItems="center" spacing={1}>
          <Grid>
            <img
              style={{ verticalAlign: 'middle' }}
              alt="workvivo_icon"
              src={getAppImage('wv_Icon.png', 48).src}
            />
          </Grid>
          <Grid>
            <Typography variant="subtitle2">{t('admin:workvivo')}</Typography>
            <Typography color="textSecondary">{t('admin:workvivoAction')}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid xs={4}>
        <Select
          variant="standard"
          fullWidth
          value={selectedWorkspace.workspace_name || ''}
          inputProps={{ id: 'space-select' }}
          onChange={handleSpaceChange}
          displayEmpty
        >
          <MenuItem value="">{t('admin:workvivoChoose')}</MenuItem>
          {workvivo_workspaces?.map((space: WorkvivoWorkspace) => (
            <MenuItem key={space.workvivo_workspace_id} value={space.workspace_name}>
              {space.workspace_name}
            </MenuItem>
          ))}
        </Select>
      </Grid>
    </Grid>
  );
};

export default WebhookItem;
